/*
 *  Notification component
 *
 *  Usage:
 *    - Add <notification></notification> tag
 *    - Add a subscription in the main component that uses it
 *    - Import CommonService and NotificationComponent from core
 *          private receiveNotification() {
 *                let method: string;
 *
 *                this.eventNotification = this._commonService.notificationEv$.subscribe(
 *                    (notification: NotificationModel) => {
 *                        if (notification.detail !== '') {
 *                            method = notification.severity || 'error';
 *                            this.notification[method](notification);
 *                        }
 *                    }
 *                );
 *            }
 *
 *            ngOnInit() {
 *                this.receiveNotification();
 *            }
 *
 *    - Call to sendNotification function in common service
 *        this._commonService.sendNotification({severity: severity, summary: '', detail: 'My notification text'})
 *
 *    - Only detail property is mandatory
 *
 *  Options:
 *      severity: string. success|error|warn|info   'error' by default
 *      summary: string.  Header text. If not exists shows a default title
 *      detail: string.  Body text. Mandatory
 *      time: number.  Time to display a message in milliseconds before removing it. 5000ms by default
 *      position: string.  Notification position. top-left|top-center|top-right
 *
 */

import { Component, ViewEncapsulation } from '@angular/core';
import { MessageService } from 'primeng/api';
import { NotificationModel } from './notificationModel.interface';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'notification',
  template:
    '<div class="notification"><p-toast [styleClass]="className" position="top-center"></p-toast></div>',
  styleUrls: ['./notification.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class NotificationComponent {
  // public msgs: NotificationModel[] = [];
  public time: number = 5000;
  public className: string;

  constructor(private messageService: MessageService) {}

  private show(notification: NotificationModel) {
    this.messageService.add({
      severity: notification.severity,
      summary: notification.summary,
      detail: notification.detail,
      life: notification.time,
      key: notification.key,
    });
  }

  private setOptions(data: NotificationModel) {
    this.time = data.time ? data.time : this.time;
    this.className = data.position || 'top-center';
  }

  public success(data: NotificationModel) {
    let notification: NotificationModel = {
      severity: 'success',
      summary: data.summary || 'Success',
      detail: data.detail,
      key: data.key,
    };

    this.setOptions(data);
    this.show(notification);
  }

  public info(data: NotificationModel) {
    let notification: NotificationModel = {
      severity: 'info',
      summary: data.summary || 'Info',
      detail: data.detail,
      key: data.key,
    };

    this.setOptions(data);
    this.show(notification);
  }

  public warn(data: NotificationModel) {
    let notification: NotificationModel = {
      severity: 'warn',
      summary: data.summary || 'Warning',
      detail: data.detail,
      key: data.key,
    };

    this.setOptions(data);
    this.show(notification);
  }

  public error(data: NotificationModel) {
    let notification: NotificationModel = {
      severity: 'error',
      summary: data.summary || 'Error',
      detail: data.detail,
      key: data.key,
    };

    this.setOptions(data);
    this.show(notification);
  }

  public clear() {
    this.messageService.clear();
  }

  public close() {
    this.time = 5000;
  }
}
